import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Image, Video, Trash2, Loader2, Scissors, Play, Pause } from 'lucide-react';
import axios from 'axios';

/**
 * LiveMobileSettingsModal - Settings for customizing Live Mobile background
 * 
 * Features:
 * - Upload custom background image
 * - Upload custom background video (like Spotify Canvas)
 * - Video trimming - select 3-10 second clip
 * - Preview current background
 * - Delete backgrounds
 */
const LiveMobileSettingsModal = ({ 
  isOpen, 
  onClose, 
  currentImage, 
  currentVideo,
  onBackgroundUpdate 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Video trimming state
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(10);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTrimmer, setShowTrimmer] = useState(false);
  
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const videoPreviewRef = useRef(null);

  // Cleanup video URL on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  // Update video preview playback position
  useEffect(() => {
    if (videoPreviewRef.current && showTrimmer) {
      const video = videoPreviewRef.current;
      
      const handleTimeUpdate = () => {
        if (video.currentTime >= trimEnd) {
          video.currentTime = trimStart;
          if (isPlaying) video.play();
        }
      };
      
      video.addEventListener('timeupdate', handleTimeUpdate);
      return () => video.removeEventListener('timeupdate', handleTimeUpdate);
    }
  }, [trimStart, trimEnd, isPlaying, showTrimmer]);

  if (!isOpen) return null;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - expanded for HEIC/HEIF
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|heic|heif)$/i)) {
      setError('Please upload a JPEG, PNG, WebP, GIF, or HEIC image');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    setIsUploading(true);
    setUploadType('image');
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/live-mobile/background/image', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Clear video when image is uploaded (either/or logic)
        onBackgroundUpdate?.({ image: response.data.background_url, video: null });
        setSuccessMessage('Image uploaded successfully!');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      setUploadType(null);
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - expanded for phone videos
    const validExtensions = /\.(mp4|mov|webm|m4v|3gp|avi|mpeg|mpg)$/i;
    const validTypes = [
      'video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v',
      'video/3gpp', 'video/x-msvideo', 'video/mpeg'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(validExtensions)) {
      setError('Unsupported video format. Please upload MP4, MOV, WebM, or similar video.');
      return;
    }

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      setError('Video must be less than 100MB');
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setVideoFile(file);
    
    // Create preview URL
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setShowTrimmer(true);
  };

  const handleVideoLoaded = () => {
    if (videoPreviewRef.current) {
      const duration = videoPreviewRef.current.duration;
      setVideoDuration(duration);
      setTrimStart(0);
      setTrimEnd(Math.min(10, duration)); // Default to 10 seconds or full video
    }
  };

  const handlePlayPause = () => {
    if (videoPreviewRef.current) {
      if (isPlaying) {
        videoPreviewRef.current.pause();
      } else {
        videoPreviewRef.current.currentTime = trimStart;
        videoPreviewRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTrimStartChange = (value) => {
    const newStart = parseFloat(value);
    setTrimStart(newStart);
    if (videoPreviewRef.current) {
      videoPreviewRef.current.currentTime = newStart;
    }
    // Ensure minimum 3 seconds clip
    if (trimEnd - newStart < 3) {
      setTrimEnd(Math.min(newStart + 3, videoDuration));
    }
    // Ensure maximum 10 seconds clip
    if (trimEnd - newStart > 10) {
      setTrimEnd(newStart + 10);
    }
  };

  const handleTrimEndChange = (value) => {
    const newEnd = parseFloat(value);
    setTrimEnd(newEnd);
    // Ensure minimum 3 seconds clip
    if (newEnd - trimStart < 3) {
      setTrimStart(Math.max(0, newEnd - 3));
    }
    // Ensure maximum 10 seconds clip
    if (newEnd - trimStart > 10) {
      setTrimStart(newEnd - 10);
    }
  };

  const handleUploadTrimmedVideo = async () => {
    if (!videoFile) return;

    setIsUploading(true);
    setUploadType('video');
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', videoFile);
      formData.append('trim_start', trimStart.toFixed(2));
      formData.append('trim_end', trimEnd.toFixed(2));

      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/live-mobile/background/video', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        // Increase timeout for processing
        timeout: 180000, // 3 minutes for transcoding
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Could add progress state here if needed
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      if (response.data.success) {
        // Clear image when video is uploaded (either/or logic)
        onBackgroundUpdate?.({ video: response.data.background_url, image: null });
        const sizeSaved = response.data.original_size_mb - response.data.processed_size_mb;
        const message = sizeSaved > 0.1 
          ? `Video optimized! (${response.data.processed_size_mb.toFixed(1)}MB, saved ${sizeSaved.toFixed(1)}MB)`
          : 'Video uploaded successfully!';
        setSuccessMessage(message);
        setShowTrimmer(false);
        setVideoFile(null);
        if (videoUrl) URL.revokeObjectURL(videoUrl);
        setVideoUrl(null);
      }
    } catch (err) {
      console.error('Error uploading video:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to process video. Please try a different format or shorter clip.';
      setError(errorMsg);
    } finally {
      setIsUploading(false);
      setUploadType(null);
    }
  };

  const cancelTrimmer = () => {
    setShowTrimmer(false);
    setVideoFile(null);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setIsPlaying(false);
  };

  const handleDeleteBackground = async (mediaType) => {
    setIsUploading(true);
    setUploadType(mediaType);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/live-mobile/background/${mediaType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      onBackgroundUpdate?.({ [mediaType]: null });
      setSuccessMessage(`${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} deleted!`);
    } catch (err) {
      console.error(`Error deleting ${mediaType}:`, err);
      setError(err.response?.data?.detail || `Failed to delete ${mediaType}`);
    } finally {
      setIsUploading(false);
      setUploadType(null);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="live-mobile-settings-modal"
    >
      <div className="relative w-full max-w-sm bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-2xl overflow-hidden shadow-2xl border border-purple-500/30 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#1a1a2e] z-10">
          <h2 className="text-lg font-bold text-white">Background Settings</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            data-testid="live-mobile-settings-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}
          
          {/* Success Message */}
          {successMessage && (
            <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 text-sm">
              {successMessage}
            </div>
          )}

          {/* Video Trimmer */}
          {showTrimmer && videoUrl && (
            <div className="space-y-3 p-3 bg-white/5 rounded-xl border border-pink-500/30">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-pink-300 flex items-center gap-2">
                  <Scissors className="w-4 h-4" />
                  Trim Your Video
                </span>
                <button 
                  onClick={cancelTrimmer}
                  className="text-gray-400 hover:text-white text-xs"
                >
                  Cancel
                </button>
              </div>
              
              {/* Video Preview */}
              <div className="relative rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-48">
                <video
                  ref={videoPreviewRef}
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  onLoadedMetadata={handleVideoLoaded}
                  playsInline
                  muted
                />
                <button
                  onClick={handlePlayPause}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition"
                >
                  {isPlaying ? (
                    <Pause className="w-10 h-10 text-white" />
                  ) : (
                    <Play className="w-10 h-10 text-white" />
                  )}
                </button>
              </div>
              
              {/* Trim Controls */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Clip: {formatTime(trimEnd - trimStart)}</span>
                  <span>3-10 seconds</span>
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Start: {formatTime(trimStart)}</label>
                  <input
                    type="range"
                    min="0"
                    max={Math.max(0, videoDuration - 3)}
                    step="0.1"
                    value={trimStart}
                    onChange={(e) => handleTrimStartChange(e.target.value)}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
                  />
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">End: {formatTime(trimEnd)}</label>
                  <input
                    type="range"
                    min="3"
                    max={videoDuration}
                    step="0.1"
                    value={trimEnd}
                    onChange={(e) => handleTrimEndChange(e.target.value)}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
                  />
                </div>
              </div>
              
              {/* Upload Button */}
              <button
                onClick={handleUploadTrimmedVideo}
                disabled={isUploading}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 rounded-xl text-white font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing video...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload & Trim ({formatTime(trimEnd - trimStart)})
                  </>
                )}
              </button>
              
              <p className="text-xs text-green-400 text-center">
                ✅ Your video will be trimmed and optimized for web playback
              </p>
            </div>
          )}

          {/* Background Type Selector - Either/Or */}
          {!showTrimmer && (
            <div className="space-y-4">
              {/* Current Status */}
              <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                <p className="text-xs text-gray-400 mb-1">Current Background</p>
                <p className="text-sm font-medium text-white">
                  {currentImage ? '📷 Custom Image' : currentVideo ? '🎬 Custom Video' : '🎥 Default Video'}
                </p>
                {!currentImage && !currentVideo && (
                  <p className="text-xs text-gray-500 mt-1">Upload an image or video to customize</p>
                )}
              </div>

              {/* Divider with "Choose One" */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Choose One</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              {/* Image Upload Section */}
              <div className={`space-y-2 p-3 rounded-xl border transition ${currentImage ? 'bg-purple-500/10 border-purple-500/50' : 'bg-white/5 border-white/10'}`}>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <Image className="w-4 h-4" />
                  Background Image
                  {currentImage && <span className="text-xs text-purple-400 ml-auto">● Active</span>}
                </label>
                
                <div className="flex items-center gap-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-xl text-purple-300 font-medium transition disabled:opacity-50"
                  >
                    {isUploading && uploadType === 'image' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5" />
                    )}
                    {currentImage ? 'Change Image' : 'Upload Image'}
                  </button>

                  {currentImage && (
                    <button
                      onClick={() => handleDeleteBackground('image')}
                      disabled={isUploading}
                      className="p-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-xl text-red-400 transition disabled:opacity-50"
                      title="Delete image (reverts to default video)"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
                
                {currentVideo && !currentImage && (
                  <p className="text-xs text-gray-500">Uploading an image will replace your current video</p>
                )}
              </div>

              {/* Video Upload Section */}
              <div className={`space-y-2 p-3 rounded-xl border transition ${currentVideo ? 'bg-pink-500/10 border-pink-500/50' : 'bg-white/5 border-white/10'}`}>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <Video className="w-4 h-4" />
                  Background Video
                  {currentVideo && <span className="text-xs text-pink-400 ml-auto">● Active</span>}
                </label>
                
                <div className="flex items-center gap-2">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/3gpp,.mp4,.mov,.webm,.m4v,.3gp"
                    onChange={handleVideoSelect}
                    className="hidden"
                  />
                  
                  <button
                    onClick={() => videoInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-500/50 rounded-xl text-pink-300 font-medium transition disabled:opacity-50"
                  >
                    {isUploading && uploadType === 'video' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5" />
                    )}
                    {currentVideo ? 'Change Video' : 'Upload Video'}
                  </button>

                  {currentVideo && (
                    <button
                      onClick={() => handleDeleteBackground('video')}
                      disabled={isUploading}
                      className="p-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-xl text-red-400 transition disabled:opacity-50"
                      title="Delete video (reverts to default video)"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
                
                {currentImage && !currentVideo && (
                  <p className="text-xs text-gray-500">Uploading a video will replace your current image</p>
                )}
                
                <p className="text-xs text-gray-500">
                  3-10 second loop • Spotify Canvas style
                </p>
              </div>
            </div>
          )}

          {/* Tips */}
          {!showTrimmer && (
            <div className="mt-4 p-3 bg-white/5 rounded-xl">
              <p className="text-xs text-gray-400">
                <span className="text-purple-400 font-semibold">Tip:</span> Choose either an image or video for your background. 
                Deleting both will revert to the default video.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveMobileSettingsModal;
